import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  TagIcon,
  BuildingStorefrontIcon,
  CurrencyRupeeIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const ProductFilters = ({
  categories,
  brands,
  selectedCategory,
  selectedBrands = [],
  minPrice,
  maxPrice,
  onFilterChange,
  onBrandChange,
  onClearFilters,
  isLoading = false,
}) => {
  const [isExpanded, setIsExpanded] = useState({
    categories: false,
    brands: false,
    price: false,
  });
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');
  const brandSelectRef = useRef(null);

  const toggleSection = (section) => {
    setIsExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const clearFilter = (filterType) => {
    onFilterChange(filterType, '');
  };

  const sortedBrands = useMemo(
    () => [...brands].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [brands]
  );

  const filteredBrands = useMemo(() => {
    const query = brandSearch.trim().toLowerCase();
    if (!query) return sortedBrands;
    return sortedBrands.filter((brand) => brand.name.toLowerCase().includes(query));
  }, [sortedBrands, brandSearch]);

  const hasActiveFilters =
    selectedCategory || selectedBrands.length > 0 || minPrice || maxPrice;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandSelectRef.current && !brandSelectRef.current.contains(event.target)) {
        setBrandDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleBrand = (brandName) => {
    const nextBrands = selectedBrands.includes(brandName)
      ? selectedBrands.filter((name) => name !== brandName)
      : [...selectedBrands, brandName];
    onBrandChange(nextBrands);
  };

  const removeBrand = (brandName) => {
    onBrandChange(selectedBrands.filter((name) => name !== brandName));
  };

  const brandTriggerLabel =
    selectedBrands.length === 0
      ? 'Select brands'
      : selectedBrands.length === 1
        ? selectedBrands[0]
        : `${selectedBrands.length} brands selected`;

  const SectionHeader = ({ icon: Icon, title, section }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="flex items-center justify-between w-full text-left mb-3 group"
    >
      <h4 className="font-semibold text-slate-900 flex items-center gap-2 text-sm">
        <Icon className="w-4 h-4 text-primary-500" />
        {title}
      </h4>
      <ChevronDownIcon
        className={`w-4 h-4 text-slate-400 transition-transform duration-200 group-hover:text-slate-600 ${
          isExpanded[section] ? 'rotate-180' : ''
        }`}
      />
    </button>
  );

  return (
    <div className="store-filter-panel">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-display font-bold text-slate-900 flex items-center gap-2">
          <FunnelIcon className="w-5 h-5 text-primary-600" />
          Filters
        </h3>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="text-xs font-semibold text-primary-600 hover:text-primary-800 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Categories */}
      <div className="store-filter-section">
        <SectionHeader icon={TagIcon} title="Category" section="categories" />
        {isExpanded.categories && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
            className="space-y-1 max-h-48 overflow-y-auto pr-1"
          >
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-9 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              categories.map((cat) => (
                <label
                  key={cat.id}
                  className={`store-filter-option ${
                    selectedCategory === cat.name ? 'store-filter-option-active' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.name}
                    checked={selectedCategory === cat.name}
                    onChange={(e) => onFilterChange('category', e.target.value)}
                    className="w-4 h-4 text-primary-600 border-slate-300 focus:ring-primary-500"
                  />
                  <span>{cat.name}</span>
                </label>
              ))
            )}
          </motion.div>
        )}
      </div>

      {/* Brands */}
      <div className="store-filter-section">
        <SectionHeader icon={BuildingStorefrontIcon} title="Brand" section="brands" />
        {isExpanded.brands && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
            className="space-y-3"
          >
            {isLoading ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-9 bg-slate-100 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                <div className="store-brand-select" ref={brandSelectRef}>
                  <button
                    type="button"
                    onClick={() => setBrandDropdownOpen((open) => !open)}
                    className="store-brand-select-trigger"
                    aria-expanded={brandDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    <span className={`truncate ${selectedBrands.length === 0 ? 'text-slate-400' : 'text-slate-800'}`}>
                      {brandTriggerLabel}
                    </span>
                    <ChevronDownIcon
                      className={`w-4 h-4 text-slate-400 shrink-0 transition-transform duration-200 ${
                        brandDropdownOpen ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {brandDropdownOpen && (
                    <div className="store-brand-select-menu">
                      <div className="relative">
                        <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                          type="text"
                          value={brandSearch}
                          onChange={(e) => setBrandSearch(e.target.value)}
                          placeholder="Search brands..."
                          className="store-brand-select-search pl-9"
                          autoFocus
                        />
                      </div>
                      <div className="store-brand-select-list" role="listbox" aria-multiselectable="true">
                        {filteredBrands.length === 0 ? (
                          <p className="store-brand-select-empty">No brands match your search</p>
                        ) : (
                          filteredBrands.map((brand) => {
                            const isSelected = selectedBrands.includes(brand.name);
                            return (
                              <label
                                key={brand.id}
                                className={`store-filter-option ${isSelected ? 'store-filter-option-active' : ''}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleBrand(brand.name)}
                                  className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                                />
                                <span>{brand.name}</span>
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {selectedBrands.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {[...selectedBrands]
                      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                      .map((brandName) => (
                        <span key={brandName} className="store-chip">
                          <span className="truncate max-w-[140px]">{brandName}</span>
                          <button
                            type="button"
                            onClick={() => removeBrand(brandName)}
                            className="p-0.5 hover:text-primary-900"
                            aria-label={`Remove ${brandName}`}
                          >
                            <XMarkIcon className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* Price */}
      <div className="store-filter-section">
        <SectionHeader icon={CurrencyRupeeIcon} title="Price Range" section="price" />
        {isExpanded.price && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Min (₹)</label>
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => onFilterChange('minPrice', e.target.value)}
                  placeholder="0"
                  className="admin-input py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Max (₹)</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => onFilterChange('maxPrice', e.target.value)}
                  placeholder="10000"
                  className="admin-input py-2"
                />
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-slate-500 mb-2">Quick select</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Under ₹500', min: '', max: '500' },
                  { label: '₹500 – ₹1K', min: '500', max: '1000' },
                  { label: '₹1K – ₹5K', min: '1000', max: '5000' },
                  { label: 'Over ₹5K', min: '5000', max: '' },
                ].map((range) => (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => {
                      onFilterChange('minPrice', range.min);
                      onFilterChange('maxPrice', range.max);
                    }}
                    className="store-price-pill"
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Active summary */}
      {hasActiveFilters && (
        <div className="pt-4 mt-2 border-t border-slate-100 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Applied</p>
          {selectedCategory && (
            <div className="store-chip justify-between w-full">
              <span className="truncate">{selectedCategory}</span>
              <button type="button" onClick={() => clearFilter('category')} className="p-0.5 hover:text-primary-900">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}
          {selectedBrands.map((brandName) => (
            <div key={brandName} className="store-chip justify-between w-full">
              <span className="truncate">{brandName}</span>
              <button type="button" onClick={() => removeBrand(brandName)} className="p-0.5 hover:text-primary-900">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
          {(minPrice || maxPrice) && (
            <div className="store-chip justify-between w-full">
              <span className="truncate">
                ₹{minPrice || '0'} – ₹{maxPrice || '∞'}
              </span>
              <button
                type="button"
                onClick={() => {
                  clearFilter('minPrice');
                  clearFilter('maxPrice');
                }}
                className="p-0.5 hover:text-primary-900"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductFilters;
